import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, FileDown } from 'lucide-react';
import { exportTableToPDF, exportSummaryReportToPDF } from '../../utils/pdfExport';
import { apiClient } from '../../utils/apiHelper';
import Pagination from '@/components/Pagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExpensesReport = () => {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({ total: 0, byCategory: {}, byMode: {} });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [collegeInfo, setCollegeInfo] = useState(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchReport();
    fetchCollegeInfo();
  }, [currentPage, itemsPerPage]);

  const fetchCollegeInfo = async () => {
    try {
      const response = await apiClient.get('/api/tenants/my-college/settings');
      setCollegeInfo(response.data);
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('from_date', startDate);
      if (endDate) params.append('to_date', endDate);
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      
      const response = await apiClient.get(`/api/accounts/expenses?${params.toString()}`);
      
      // apiClient interceptor unwraps: response.data = items array, response.pagination = {total, stats, ...}
      const expensesData = Array.isArray(response.data) ? response.data : [];
      const pagination = response.pagination || {};
      const serverStats = pagination.stats || {};
      
      setExpenses(expensesData);
      setTotalItems(pagination.total || 0);
      setSummary({
        total: serverStats.total_amount || 0,
        byCategory: serverStats.by_category || {},
        byMode: serverStats.by_mode || {}
      });
    } catch (error) {
      console.error('Error:', error);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleFilterApply = () => {
    setCurrentPage(1);
    fetchReport();
  };

  const [exporting, setExporting] = useState(false);

  const exportToPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('from_date', startDate);
      if (endDate) params.append('to_date', endDate);
      params.append('page', 1);
      params.append('limit', 5000);
      
      const response = await apiClient.get(`/api/accounts/expenses?${params.toString()}`);
      const allExpenses = Array.isArray(response.data) ? response.data : [];
      const paginationStats = response.pagination?.stats || {};
      const totalAmount = paginationStats.total_amount || allExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
      
      if (allExpenses.length === 0) {
        alert('No data to export');
        return;
      }

      const byCategory = allExpenses.reduce((acc, exp) => {
        acc[exp.category] = (acc[exp.category] || 0) + exp.amount;
        return acc;
      }, {});

      exportSummaryReportToPDF('Expenses Report', {
        'Total Expenses': `Rs. ${totalAmount.toFixed(2)}`,
        'Total Transactions': allExpenses.length,
        'Categories': Object.keys(byCategory).length
      }, [
        { header: 'Date', dataKey: 'expense_date' },
        { header: 'Category', dataKey: 'category' },
        { header: 'Amount', dataKey: 'amount' },
        { header: 'Payment Mode', dataKey: 'payment_mode' },
        { header: 'Description', dataKey: 'description' }
      ], allExpenses.map(exp => ({
        expense_date: exp.expense_date || 'N/A',
        category: exp.category || 'N/A',
        amount: `Rs. ${(exp.amount || 0).toFixed(2)}`,
        payment_mode: exp.payment_mode || 'N/A',
        description: exp.description || ''
      })), 'expenses_report.pdf', collegeInfo);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Expenses Report</CardTitle>
          <div className="flex gap-4 items-end">
            <div><Label>Start Date</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
            <div><Label>End Date</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></div>
            <Button onClick={fetchReport} disabled={loading}><FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Generate'}</Button>
            <Button onClick={exportToPDF} className="bg-green-600 hover:bg-green-700" disabled={loading || !expenses || expenses.length === 0 || exporting}>
              <FileDown className="w-4 h-4 mr-2" />Export PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Expenses</p>
              <p className="text-2xl font-bold text-red-600">₹{summary.total.toFixed(2)}</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Transactions</p>
              <p className="text-2xl font-bold text-orange-600">{totalItems}</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm text-gray-600">Categories</p>
              <p className="text-2xl font-bold text-yellow-600">{Object.keys(summary.byCategory).length}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Expenses by Category</h3>
              <div className="space-y-2">
                {Object.entries(summary.byCategory).map(([category, amount]) => (
                  <div key={category} className="flex justify-between p-3 border rounded-lg">
                    <span className="text-gray-700">{category}</span>
                    <span className="font-bold text-red-600">₹{amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3">Expenses by Payment Mode</h3>
              <div className="space-y-2">
                {Object.entries(summary.byMode).map(([mode, amount]) => (
                  <div key={mode} className="flex justify-between p-3 border rounded-lg">
                    <span className="text-gray-700">{mode}</span>
                    <span className="font-bold text-orange-600">₹{amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <h3 className="text-lg font-semibold mb-3">Recent Expense Transactions</h3>
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Amount</th>
                <th className="text-left p-3">Payment Mode</th>
                <th className="text-left p-3">Description</th>
              </tr>
            </thead>
            <tbody>
              {expenses.slice(0, 20).map((expense, idx) => (
                <tr key={idx} className="border-b hover:bg-gray-50">
                  <td className="p-3">{expense.expense_date}</td>
                  <td className="p-3">{expense.category}</td>
                  <td className="p-3 font-semibold text-red-600">₹{expense.amount}</td>
                  <td className="p-3">{expense.payment_mode}</td>
                  <td className="p-3">{expense.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalItems / itemsPerPage)}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default ExpensesReport;